import regex from '@/regex';
import {testScript} from '@/util/testScript';
import {isTrue, matches, maxLength, notEmpty, check, type FormSchema} from '@/util/validation';

// Form validation schemas. Each field lists its rules in the same order as the yup chain it
// replaced, so the first failing rule — the message the visitor sees — is unchanged.
// Kept out of the `@/constants` barrel so the forms' code stays in their lazy chunks.

interface NameEmailAbout {
    fullName: string;
    email: string;
    about: string;
    captcha: boolean;
}

interface WithPhone extends NameEmailAbout {
    phone_number: string | number;
    country_code: string | number;
}

const fullNameSpec = {
    required: 'Please enter full name',
    trim: true,
    rules: [
        matches(regex.NOT_NUMBER, 'Please enter valid full name'),
        testScript('Please enter valid full name'),
        notEmpty('Please enter full name'),
        maxLength(50, 'Please enter name less than 50 character')
    ]
};

const emailSpec = {
    required: 'Please enter an email',
    rules: [notEmpty('Please enter an email'), matches(regex.EMAIL, 'Please enter an valid email address')]
};

const captchaSpec = {rules: [isTrue('Google captcha is required')]};

const aboutSpec = (requiredMessage: string) => ({
    required: requiredMessage,
    trim: true,
    rules: [testScript('Please enter valid comment'), notEmpty(requiredMessage)]
});

const phoneSpec = {
    required: 'Please enter phone number',
    rules: [
        notEmpty('Please enter phone number'),
        check<WithPhone>((value, values) => {
            // `.length` on purpose (not String()): a numeric code has no length, so nothing is sliced — as before.
            const countryCode = values.country_code as {length?: number} | undefined;
            const phoneNumber = String(value).slice(countryCode?.length || 0);
            return !!phoneNumber && phoneNumber.length <= 15;
        }, 'Please enter valid number')
    ]
};

/** "Talk with our expert" and the contact page form. */
export const mainValidationSchema: FormSchema<WithPhone> = {
    fullName: fullNameSpec,
    email: emailSpec,
    captcha: captchaSpec,
    about: aboutSpec('Please enter about your project'),
    phone_number: phoneSpec
};

/** Footer "project brief" form: the main fields plus service and budget. */
export const contactValidationSchema: FormSchema<WithPhone & {budget: string; serviceNeeded: string}> = {
    ...mainValidationSchema,
    budget: {required: 'Please select budget', rules: [notEmpty('Please select budget')]},
    serviceNeeded: {required: 'Please select a service', rules: [notEmpty('Please select a service')]}
};

/** Hero lead form: the main fields minus phone, plus an optional company, service and budget. */
export const heroLeadValidationSchema: FormSchema<NameEmailAbout & {company: string; serviceNeeded: string; budget: string}> = {
    fullName: fullNameSpec,
    email: emailSpec,
    captcha: captchaSpec,
    about: aboutSpec('Please enter about your project'),
    company: {
        trim: true,
        rules: [testScript('Please enter valid company name'), maxLength(100, 'Please enter company name less than 100 character')]
    },
    serviceNeeded: {required: 'Please select a service', rules: [notEmpty('Please select a service')]},
    budget: {required: 'Please select budget', rules: [notEmpty('Please select budget')]}
};

/** Blog comment form. */
export const commentValidationSchema: FormSchema<NameEmailAbout> = {
    fullName: fullNameSpec,
    email: emailSpec,
    captcha: captchaSpec,
    about: aboutSpec('Please enter your comment')
};
